/* eslint-disable indent */
import { createSlice } from '@reduxjs/toolkit';
import api from 'src/api/index';
import i18n from 'src/i18n';
import { showErrorToast, showSuccessToast } from './toast';


export const webinarsErrorMessage = (message) => {
	const t = i18n.t.bind(i18n);
	switch (message) {
		case 'webinar already planned for this period' 
			: return t('toasts.webinarAlreadyPlanned');
		case 'new webinar must start at least 48h0m0s from now'
			: return t('toasts.webinarMustStart48h');
		case 'webinar URL not provided'
			: return t('toasts.webinarUrlNotProvided');
		case 'can\'t edit url with that broadcast type'
			: return t('toasts.webinarCantEditUrlKinescope');
		default: return t('toasts.webinarCreateError');
	}
};

const initialState = {
	list: [],
	total: 0,
	loading: false,
	draft: null
};

const webinarsSlice = createSlice({
	name: 'webinars',
	initialState,
	reducers: {
		
		setWebinarsList: (state, action) => {
			state.list = action.payload.list;
			state.total = action.payload.count;
		},

		addWebinarsItem: (state, action) => {
			state.list.push(action.payload);
		},

		removeWebinarsItem: (state, action) => {
			state.list = state.list.filter((item) => action.payload !== item.id);
		},

		updateWebinarItem: (state, action) => {
			const index = state.list.findIndex((item) => item.id === action.payload.id);
			if (index !== -1) {
				state.list.splice(index, 1, action.payload);
			}
		},

		setWebinarsLoading: (state, action) => {
			state.loading = action.payload;
		},

		setCashedWebinar: ( state, action ) => {
			if (state.draft) {
				state.draft = { ...state.draft, ...action.payload };
			} else {
				state.draft = action.payload;
			}
		},

		removeCashedWebinar: ( state ) => {
			state.draft = null;
		}
		
	},
});

export const { setWebinarsList, addWebinarsItem, 
	removeWebinarsItem, updateWebinarItem, 
	setWebinarsLoading, removeCashedWebinar, setCashedWebinar } = webinarsSlice.actions;


export const fetchWebinarList = ( payload ) => async (dispatch) => {

	dispatch(setWebinarsLoading(true));

	try {

		const res = await api.webinarsList( payload );
		dispatch(setWebinarsList(res));

	} catch (error) {

		dispatch(showErrorToast({
			description: i18n.t('toasts.failedGetWebinars')
		}));

		throw error;

	} finally {

		dispatch(setWebinarsLoading(false));

	}

};

export const getWebinarById = (id) => (dispatch, getState) => {

	return new Promise((resolve, reject) => {

		const state = getState();

		const list = state.webinars.list;

		let item = list.find((item) => item.id === id);

		if (item) {
			resolve(item);
		} else {
			api.webinarById(id)
				.then((res) => {
					dispatch(addWebinarsItem(res));
					resolve(res);
				})
				.catch(() => {
					dispatch(
						showErrorToast({
							description: i18n.t('toasts.webinarNotFound', { id }),
						})
					);

					reject();
				});
		}
	});
};

export const editWebinar = ( webinar, skipToast = false ) => async ( dispatch ) => {

	try {

		const res = await api.editWebinar( webinar.id, webinar );

		if (res) {

			await dispatch(reloadWebinar(webinar.id, false)); // Не показываем тост

		}

	} catch (error) {

		console.error(error);

		// Показываем тост только если не передан флаг skipToast
		if (!skipToast) {
			dispatch(
				showErrorToast({
					title: i18n.t('toasts.error'),
					description: i18n.t('toasts.failedUpdateWebinar'),
				})
			);
		}

		throw error;

	}

};

export const cancelWebinar = (webinarId) => async (dispatch) => {

	try {

		const res = await api.cancelWebinar( webinarId );

		if (res) {

			await dispatch(reloadWebinar( webinarId, false ));
			
			dispatch(showSuccessToast({
				title: i18n.t('toasts.success'),
				description: i18n.t('toasts.webinarCancelled')
			}));

		}

	} catch (error) {

		console.error(error);

		dispatch(
			showErrorToast({
				description: i18n.t('toasts.failedCancelWebinar'),
			})
		);

		throw error;

	}


};

export const editWebinarPublication = ( { publicationId, webinarId, payload } ) => async (dispatch) => {

	dispatch( setWebinarsLoading(true) );

	try {

		const res = await api.editWebinarPublication( publicationId, payload );

		if (res && webinarId) {

			await dispatch(reloadWebinar( webinarId, false )); // Не показываем тост

		}

	} catch (error) {

		console.error(error);

		dispatch(
			showErrorToast({
				description: i18n.t('toasts.failedUpdateWebinar'),
			})
		);

		throw error;

	} finally {

		dispatch( setWebinarsLoading(false) );

	}

};

export const reloadWebinar = (webinarId, showToast = true) => async (dispatch) => {
	
	dispatch(setWebinarsLoading(true));

	try {

		const webinar = await api.webinarById(webinarId);

		if (webinar) {

			dispatch(updateWebinarItem(webinar));

			if (showToast) {
				dispatch(showSuccessToast({
					title: i18n.t('toasts.success'),
					description: i18n.t('toasts.webinarUpdated')
				}));
			}


		}

	} catch (error) {

		console.error(error);

		dispatch(
			showErrorToast({
				description: i18n.t('toasts.failedUpdateWebinar'),
			})
		);

		throw error;

	} finally {

		dispatch(setWebinarsLoading(false));

	}

};

export const checkCanStartWebinar = ( webinarId ) => async ( dispatch ) => {

	try {
		
		const res = await api.checkCanStartWebinar( webinarId );
		
		return res;

	} catch (error) {

		console.error(error);
		return false;

	}

};

export const startWebinar = ( webinarId ) => async ( dispatch ) => {

	try {
		
		const res = await api.startWebinar( webinarId );
		
		if (res) {

			dispatch(showSuccessToast({
				title: i18n.t('toasts.success'),
				description: i18n.t('toasts.webinarStartNotifSent')
			}));

			return res;

		}

	} catch (error) {

		console.error(error);

		const errorText = error?.response?.data === 'webinar URL not provided'
			? i18n.t('toasts.webinarUrlNotProvided')
			: i18n.t('toasts.webinarStartNotifError');

		dispatch(
			showErrorToast({
				description: errorText,
			})
		);

		throw error;
		
	}

};


export const finishWebinar = ( webinarId ) => async ( dispatch ) => {

	try {
		
		const res = await api.finishWebinar( webinarId );
		
		if (res) {

			await dispatch(reloadWebinar( webinarId, false ));

			dispatch(showSuccessToast({
				title: i18n.t('toasts.success'),
				description: i18n.t('toasts.webinarFinished')
			}));

			return res;

		}

	} catch (error) {

		console.error(error);

		dispatch(
			showErrorToast({
				description: i18n.t('toasts.failedFinishWebinar'),
			})
		);

		throw error;
		
	}

};


export default webinarsSlice.reducer;
